import React, { Component } from 'react';
import Swal from 'sweetalert2';
import { RiArrowGoBackFill } from 'react-icons/ri';
import { Menu } from '../../../components/Menu/Menu';
import { getValueCookie } from '../../../services/cookieService';
import {
    COMPANIES, ORDER_TYPES, MOCK_MODES, anySimulated, getMockMode, setMockMode,
    listProductLines, listVendors, getNextProductLine, getNextVendorNo, lookupInSage,
    checkPortalDuplicates, searchItemCodes, createPortalItem, uploadItemImage, getPricingCompanies, savePortalPricing,
} from '../../../services/newItemWizardService';
import { phoneDigits } from '../../Maintenance/Vendors';
import '../../../css/general-style.css';
import '../../../css/dashboard.css';
import { C, CARD, H2, SUB, BTN_PRIMARY, BTN_SECONDARY, btn, Notice } from './ui';
import {
    newDraft, setCompanies, loadDraft, saveDraft, clearDraft, normalizeResumedDraft, anythingSent,
    buildSteps, isStepComplete, maxReachableIndex, isLocked, plOf, vendorOf, companyOrder, companyName,
    validateProductLineForm, validateVendorForm, validateProductForm, validatePricesForm, levelOrderWarnings, priceFloor,
    matchProductLine, matchVendor, matchItem, matchCost, runSageTask, emptyTask, emptyPortalTask,
    IMAGE_MAX_BYTES, IMAGE_TYPES, suggestNextCode, sortCodesDesc, isItemCodeFormatOk, isUpcFormatOk,
} from './wizardLogic';
import CompaniesStep from './CompaniesStep';
import CatalogEntityStep from './CatalogEntityStep';
import ItemStep from './ItemStep';
import PricesStep from './PricesStep';
import SummaryView from './SummaryView';

const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const sessionCompany = () => (getValueCookie('CompanyId') === '6' ? 6 : 3);

const emptyList = () => ({ loading: false, loaded: false, rows: [], error: '' });
const idleCheck = () => ({ status: 'idle', value: '', message: '' });
const CHECK_DEBOUNCE_MS = 600;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;
const emptySearch = () => ({ query: '', loading: false, loaded: false, rows: [], total: 0, error: '' });

/**
 * Inventory → New Item (wizard multiempresa), 2026-10-14.
 *
 * Companies → por empresa (principal primero): Product Line → Vendor →
 * Item → Prices & Cost → Summary. Cada escritura en Sage se verifica
 * leyéndola de vuelta (ver runSageTask) antes de habilitar "Next", y el
 * avance se guarda como borrador en localStorage para reanudar tras un
 * corte. Endpoints y payloads: docs/new-item-wizard-endpoints.md.
 */
export default class NewItemWizard extends Component {
    constructor(props) {
        super(props);
        this.userName = getValueCookie('userName');
        this.state = {
            draft: null,
            resumeCandidate: null,
            finished: false,
            busy: false,
            lists: { productLine: {}, vendor: {} },
            numbers: {},       // { 'productLine-3': { loading, error } }
            errors: {},        // { [stepId]: { field: msg } }
            dupError: '',
            dupWarning: '',
            imageFile: null,   // { dataUrl, name, size } — solo en memoria
            imageError: '',
            parentOf: {},      // { idCompany: parentCompanyId } desde /v3/pricing/companies
            mockMode: getMockMode(),
            showStatus: false,
            codeSearch: emptySearch(), // autocompletado del ItemCode (items de la empresa principal)
            codeCheck: idleCheck(), // disponibilidad del ItemCode mientras se escribe
            upcCheck: idleCheck(),
        };
        this._timers = [];
        this._listLoads = {};
        this._checkTimers = {};
        this._checkSeq = { itemCode: 0, upc: 0 };
        this._searchSeq = 0;
        this._unmounted = false;
        this._unloadGuard = false;
    }

    async componentDidMount() {
        const saved = loadDraft(this.userName);
        if (saved) this.setState({ resumeCandidate: normalizeResumedDraft(saved) });
        else this.setState({ draft: newDraft(sessionCompany()) });

        const companies = await getPricingCompanies();
        if (this._unmounted) return;
        const parentOf = {};
        companies.forEach((c) => { if (c.parentCompanyId) parentOf[c.idCompany] = c.parentCompanyId; });
        this.setState({ parentOf });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.draft && this.state.draft !== prevState.draft && !this.state.finished) {
            saveDraft(this.userName, this.state.draft);
        }
        if (this.state.draft && (prevState.draft === null || this.state.draft.stepId !== prevState.draft.stepId)) {
            this.onEnterStep();
        }
    }

    componentWillUnmount() {
        this._unmounted = true;
        this._timers.forEach(clearTimeout);
        Object.keys(this._checkTimers).forEach((k) => clearTimeout(this._checkTimers[k]));
        this.setUnloadGuard(false);
    }

    // ── Infra ────────────────────────────────────────────────────────────────
    wait = (ms) => new Promise((resolve) => { this._timers.push(setTimeout(resolve, ms)); });
    isCancelled = () => this._unmounted;

    handleBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    setUnloadGuard(on) {
        if (on && !this._unloadGuard) window.addEventListener('beforeunload', this.handleBeforeUnload);
        if (!on && this._unloadGuard) window.removeEventListener('beforeunload', this.handleBeforeUnload);
        this._unloadGuard = on;
    }
    setBusy(on) {
        this.setUnloadGuard(on);
        if (!this._unmounted) this.setState({ busy: on });
    }

    updateDraft(fn) {
        if (this._unmounted) return;
        this.setState((prev) => ({ draft: fn(prev.draft) }));
    }
    patchCompany(id, part, patch) {
        this.updateDraft((d) => ({
            ...d,
            companies: { ...d.companies, [id]: { ...d.companies[id], [part]: { ...d.companies[id][part], ...patch } } },
        }));
    }
    patchProduct(patch) {
        this.updateDraft((d) => ({ ...d, product: { ...d.product, ...patch } }));
    }
    setStepErrors(stepId, errs) {
        this.setState((prev) => ({ errors: { ...prev.errors, [stepId]: errs } }));
    }
    clearFieldError(stepId, field) {
        this.setState((prev) => {
            const cur = prev.errors[stepId] || {};
            if (!cur[field]) return null;
            return { errors: { ...prev.errors, [stepId]: { ...cur, [field]: undefined } } };
        });
    }

    // ── Navegación ───────────────────────────────────────────────────────────
    steps() { return buildSteps(this.state.draft); }
    currentIndex() {
        const steps = this.steps();
        const i = steps.findIndex((s) => s.id === this.state.draft.stepId);
        return i >= 0 ? i : 0;
    }
    goTo(index) {
        const steps = this.steps();
        const target = steps[Math.max(0, Math.min(index, steps.length - 1))];
        this.updateDraft((d) => ({ ...d, stepId: target.id }));
        this.setState({ dupError: '', dupWarning: '', showStatus: false });
        window.scrollTo(0, 0);
    }
    next = () => {
        const steps = this.steps();
        const i = this.currentIndex();
        if (this.state.busy || !isStepComplete(this.state.draft, steps[i])) return;
        this.goTo(i + 1);
    };
    back = () => {
        if (this.state.busy) return;
        this.goTo(this.currentIndex() - 1);
    };

    onEnterStep() {
        const step = this.steps()[this.currentIndex()];
        if (step.type === 'productLine' || step.type === 'vendor') this.ensureList(step.type, step.company);
        const d = this.state.draft;
        if (step.type === 'item' && Number(step.company) === Number(d.mainCompany) && !isLocked(d, step)) {
            this.scheduleSearch(d.product.form.itemCode, 0);
            this.scheduleCheck('itemCode', d.product.form.itemCode, 0);
            this.scheduleCheck('upc', d.product.form.upc, 0);
        }
    }

    // ── Listas y correlativos ────────────────────────────────────────────────
    // Una sola carga en vuelo por lista; assignNumber la espera para calcular
    // el correlativo con la lista completa.
    ensureList(kind, id, force) {
        const key = `${kind}-${id}`;
        const cur = this.state.lists[kind][id];
        if (this._listLoads[key]) return this._listLoads[key];
        if (cur && cur.loaded && !force) return Promise.resolve();
        this.setList(kind, id, { ...emptyList(), loading: true });
        const load = (kind === 'productLine' ? listProductLines(id) : listVendors(id)).then((res) => {
            delete this._listLoads[key];
            if (this._unmounted) return;
            this.setList(kind, id, { loading: false, loaded: res.ok, rows: res.rows, error: res.ok ? '' : res.message });
        });
        this._listLoads[key] = load;
        return load;
    }
    setList(kind, id, value) {
        this.setState((prev) => ({ lists: { ...prev.lists, [kind]: { ...prev.lists[kind], [id]: value } } }));
    }

    async assignNumber(kind, id) {
        const key = `${kind}-${id}`;
        this.setState((prev) => ({ numbers: { ...prev.numbers, [key]: { loading: true, error: '' } } }));
        await this.ensureList(kind, id);
        const list = this.state.lists[kind][id] || emptyList();
        const codes = list.rows.map((r) => r.code);
        const res = kind === 'productLine' ? await getNextProductLine(id, codes) : await getNextVendorNo(id, codes);
        if (this._unmounted) return;
        this.setState((prev) => ({ numbers: { ...prev.numbers, [key]: { loading: false, error: res.ok ? '' : res.message } } }));
        if (res.ok) {
            const c = this.state.draft.companies[id][kind];
            this.patchCompany(id, kind, { form: { ...c.form, code: res.value }, task: emptyTask() });
        }
    }

    // ── Paso: empresas ───────────────────────────────────────────────────────
    onCompanies = (main, additional) => {
        if (anythingSent(this.state.draft)) return;
        this.updateDraft((d) => setCompanies(d, main, additional));
    };

    // ── Paso: Product Line / Vendor ──────────────────────────────────────────
    onEntityMode(kind, id, mode) {
        const entity = this.state.draft.companies[id][kind];
        if (entity.task.sent) return;
        this.patchCompany(id, kind, { mode });
        this.setStepErrors(`${kind === 'productLine' ? 'pl' : 'vendor'}-${id}`, {});
        if (mode === 'new' && !entity.form.code) this.assignNumber(kind, id);
    }
    onEntitySelect(kind, id, row) {
        this.patchCompany(id, kind, { selected: row });
        this.clearFieldError(`${kind === 'productLine' ? 'pl' : 'vendor'}-${id}`, 'selected');
    }
    onEntityField(kind, id, field, value) {
        const entity = this.state.draft.companies[id][kind];
        const resetTask = !entity.task.sent && ['failed', 'conflict'].indexOf(entity.task.status) >= 0;
        this.patchCompany(id, kind, { form: { ...entity.form, [field]: value }, task: resetTask ? emptyTask() : entity.task });
        this.clearFieldError(`${kind === 'productLine' ? 'pl' : 'vendor'}-${id}`, field);
    }

    async createEntity(kind, id) {
        if (this.state.busy) return;
        const stepId = `${kind === 'productLine' ? 'pl' : 'vendor'}-${id}`;
        const entity = this.state.draft.companies[id][kind];
        const f = entity.form;
        const errs = kind === 'productLine' ? validateProductLineForm(f) : validateVendorForm(f);
        if (Object.keys(errs).length) { this.setStepErrors(stepId, errs); return; }

        // Que el nombre no exista ya con otro número (evita duplicar a mano).
        const list = this.state.lists[kind][id] || emptyList();
        const dupe = !entity.task.sent && list.rows.find((r) => (kind === 'productLine'
            ? r.desc.toUpperCase() === f.desc.trim().toUpperCase()
            : r.name.toUpperCase() === f.name.trim().toUpperCase()) && r.code !== f.code);
        if (dupe) {
            const r = await Swal.fire({
                icon: 'warning', title: 'Possible duplicate',
                html: `<b>${escapeHtml(dupe.code)} — ${escapeHtml(dupe.desc || dupe.name)}</b> already exists in ${escapeHtml(companyName(id))}.<br/>Use the existing one instead?`,
                showDenyButton: true, confirmButtonText: 'Use existing', denyButtonText: 'Create anyway', confirmButtonColor: '#2563EB', denyButtonColor: '#64748B',
            });
            if (r.isConfirmed) { this.patchCompany(id, kind, { mode: 'existing', selected: dupe }); return; }
            if (!r.isDenied) return;
        }

        const payload = kind === 'productLine'
            ? { OrderType: ORDER_TYPES.productLine, IdCompany: id, CompanyId: String(id), ProductLine: f.code, ProductLineDesc: f.desc.trim() }
            : {
                OrderType: ORDER_TYPES.vendor, IdCompany: id, idcompany: String(id), VendorNo: f.code, VendorName: f.name.trim(),
                VendorAddress: f.address.trim(), VendorCity: f.city.trim(), VendorState: f.state.trim(), VendorZip: f.zip.trim(),
                VendorCountry: 'USA', VendorPhone: phoneDigits(f.phone), VendorEmail: f.email.trim(),
            };
        const formForMatch = kind === 'productLine' ? { desc: f.desc } : { name: f.name };

        this.setBusy(true);
        await runSageTask({
            task: entity.task,
            entity: kind,
            idCompany: id,
            key: f.code,
            match: kind === 'productLine' ? matchProductLine(formForMatch) : matchVendor(formForMatch),
            payload,
            onUpdate: (task) => this.patchCompany(id, kind, { task }),
            wait: this.wait,
            isCancelled: this.isCancelled,
            conflictMessage: `${kind === 'productLine' ? 'Product line' : 'Vendor'} ${f.code} already exists in Sage with a different ${kind === 'productLine' ? 'description' : 'name'} (someone may have used this number). Get a new number.`,
        });
        this.setBusy(false);
    }

    // ── Paso: Item ───────────────────────────────────────────────────────────
    onProductField = (field, value) => {
        const d = this.state.draft;
        const mainTask = d.companies[d.mainCompany].item.task;
        const resetTask = !mainTask.sent && ['failed', 'conflict'].indexOf(mainTask.status) >= 0;
        this.updateDraft((dr) => {
            const next = { ...dr, product: { ...dr.product, form: { ...dr.product.form, [field]: value } } };
            if (!resetTask) return next;
            const c = next.companies[dr.mainCompany];
            return { ...next, companies: { ...next.companies, [dr.mainCompany]: { ...c, item: { task: emptyTask() } } } };
        });
        this.clearFieldError(`item-${d.mainCompany}`, field);
        this.setState({ dupError: '' });
        if (field === 'itemCode') this.scheduleSearch(value, SEARCH_DEBOUNCE_MS);
        if (field === 'itemCode' || field === 'upc') this.scheduleCheck(field, value, CHECK_DEBOUNCE_MS);
    };

    // ── Item code / UPC en vivo ──────────────────────────────────────────────
    // Ayuda mientras se escribe; el chequeo definitivo vuelve a correr en
    // checkDuplicates() justo antes de mandar a Sage.
    // Autocompletado: busca en TODOS los items de la empresa principal que
    // empiezan con lo escrito (mirror CI_Item), desde SEARCH_MIN_CHARS letras.
    scheduleSearch(rawValue, delay) {
        const query = String(rawValue || '').trim().toUpperCase();
        clearTimeout(this._checkTimers.search);
        const seq = ++this._searchSeq;
        if (query.length < SEARCH_MIN_CHARS) { this.setState({ codeSearch: { ...emptySearch(), query } }); return; }
        this.setState((prev) => ({ codeSearch: { ...prev.codeSearch, query, loading: true, error: '' } }));
        this._checkTimers.search = setTimeout(async () => {
            const res = await searchItemCodes(this.state.draft.mainCompany, query);
            if (this._unmounted || seq !== this._searchSeq) return;
            this.setState({ codeSearch: { query, loading: false, loaded: res.ok, rows: sortCodesDesc(res.rows), total: res.total, error: res.ok ? '' : res.message } });
            // Si el código exacto ya está en la lista, el chequeo lo marca al instante.
            const code = String(this.state.draft.product.form.itemCode || '').trim().toUpperCase();
            if (code && res.rows.some((r) => r.itemCode.toUpperCase() === code)) this.scheduleCheck('itemCode', code, 0);
        }, delay);
    }

    scheduleCheck(field, rawValue, delay) {
        const stateKey = field === 'itemCode' ? 'codeCheck' : 'upcCheck';
        const value = String(rawValue || '').trim().toUpperCase();
        clearTimeout(this._checkTimers[field]);
        const seq = ++this._checkSeq[field];
        const formatOk = field === 'itemCode' ? isItemCodeFormatOk(value) : isUpcFormatOk(value);
        if (!formatOk) { this.setState({ [stateKey]: idleCheck() }); return; }
        this.setState({ [stateKey]: { status: 'checking', value, message: '' } });
        this._checkTimers[field] = setTimeout(() => {
            if (field === 'itemCode') this.runCodeCheck(value, seq);
            else this.runUpcCheck(value, seq);
        }, delay);
    }

    async runCodeCheck(value, seq) {
        const stale = () => this._unmounted || seq !== this._checkSeq.itemCode;
        const set = (status, message) => { if (!stale()) this.setState({ codeCheck: { status, value, message } }); };
        const d = this.state.draft;
        const hit = this.state.codeSearch.rows.find((r) => r.itemCode.toUpperCase() === value);
        if (hit) return set('taken', `${value} already exists in ${companyName(d.mainCompany)}${hit.productLine ? ` (product line ${hit.productLine})` : ''}.`);
        const portal = await checkPortalDuplicates(d.mainCompany, value, '');
        if (stale()) return;
        if (portal.ok && portal.itemCodeExists) return set('taken', `${value} already exists in the portal.`);
        const unverified = portal.ok ? [] : ['the portal'];
        const ids = companyOrder(d);
        for (let i = 0; i < ids.length; i++) {
            if (d.companies[ids[i]].item.task.sent) continue;
            const res = await lookupInSage('item', ids[i], value);
            if (stale()) return;
            if (!res.ok) { unverified.push(`${companyName(ids[i])} Sage`); continue; }
            if (res.exists) return set('taken', `${value} already exists in ${companyName(ids[i])} Sage.`);
        }
        if (unverified.length) return set('unverified', `Couldn't check ${unverified.join(', ')} right now — it will be checked again before creating the item.`);
        return set('available', `${value} is available in ${ids.map(companyName).join(' and ')}.`);
    }

    async runUpcCheck(value, seq) {
        const stale = () => this._unmounted || seq !== this._checkSeq.upc;
        const d = this.state.draft;
        const portal = await checkPortalDuplicates(d.mainCompany, '', value);
        if (stale()) return;
        let check;
        if (!portal.ok) check = { status: 'unverified', value, message: "Couldn't check the UPC right now — it will be checked again before creating the item." };
        else if (portal.upcExists) check = { status: 'taken', value, message: `UPC ${value} is already used by another item.` };
        else check = { status: 'available', value, message: 'UPC not used by any other item.' };
        this.setState({ upcCheck: check });
    }

    onImage = (file) => {
        if (!file) return;
        if (IMAGE_TYPES.indexOf(file.type) < 0) { this.setState({ imageError: 'Only PNG, JPG or WEBP images.' }); return; }
        if (file.size > IMAGE_MAX_BYTES) { this.setState({ imageError: 'The image is larger than 5 MB.' }); return; }
        const reader = new FileReader();
        reader.onload = () => {
            if (this._unmounted) return;
            this.setState({ imageFile: { dataUrl: reader.result, name: file.name, size: file.size }, imageError: '' });
            this.patchProduct({ imageName: file.name, imageTask: emptyPortalTask() });
        };
        reader.onerror = () => this.setState({ imageError: 'Could not read the image file.' });
        reader.readAsDataURL(file);
    };
    onRemoveImage = () => {
        this.setState({ imageFile: null, imageError: '' });
        this.patchProduct({ imageName: '', imageTask: emptyPortalTask() });
    };

    // Antes del primer envío: el ItemCode/UPC no debe existir ni en el portal
    // ni en el Sage de ninguna empresa destino.
    async checkDuplicates() {
        const d = this.state.draft;
        const f = d.product.form;
        const portal = await checkPortalDuplicates(d.mainCompany, f.itemCode, f.upc);
        if (!portal.ok) return { error: `${portal.message} Try again.` };
        if (portal.itemCodeExists || portal.upcExists) {
            const what = [portal.itemCodeExists && `item code ${f.itemCode}`, portal.upcExists && `UPC ${f.upc}`].filter(Boolean).join(' and ');
            return { error: `The ${what} already exists in the portal. Item code and UPC must be unique.` };
        }
        const warnings = [];
        const ids = companyOrder(d);
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            if (d.companies[id].item.task.sent) continue;
            const res = await lookupInSage('item', id, f.itemCode);
            if (!res.ok) {
                if (i === 0) return { error: `Could not check ${companyName(id)} Sage for ${f.itemCode} (${res.message || 'no response'}). Try again.` };
                warnings.push(companyName(id));
                continue;
            }
            if (res.exists) return { error: `Item ${f.itemCode} already exists in ${companyName(id)} Sage. Use a different item code.` };
        }
        return { warning: warnings.length ? `Couldn't check ${warnings.join(', ')} Sage now — it will be checked again before creating the item there.` : '' };
    }

    itemPayload(id) {
        const d = this.state.draft;
        const c = d.companies[id];
        const f = d.product.form;
        return {
            OrderType: ORDER_TYPES.item,
            IdCompany: id,
            Products: [{
                ItemCode: f.itemCode,
                Description: f.description.trim(),
                ProductLineNo: plOf(c).code,
                VendorNo: vendorOf(c).code,
                UnitCost: 0,
                StandardUnitOfMeasure: f.um,
                Weight: Number(f.weight),
            }],
        };
    }

    async runItemTask(id) {
        const d = this.state.draft;
        const c = d.companies[id];
        const pl = plOf(c);
        const vendor = vendorOf(c);
        return runSageTask({
            task: c.item.task,
            entity: 'item',
            idCompany: id,
            key: d.product.form.itemCode,
            match: matchItem(pl.code, vendor.code),
            payload: this.itemPayload(id),
            onUpdate: (task) => this.patchCompany(id, 'item', { task }),
            wait: this.wait,
            isCancelled: this.isCancelled,
            conflictMessage: `Item ${d.product.form.itemCode} already exists in ${companyName(id)} Sage with a different product line or vendor.`,
        });
    }

    async runPortalItem() {
        const d = this.state.draft;
        const f = d.product.form;
        if (d.product.portalTask.status !== 'done') {
            this.patchProduct({ portalTask: { status: 'saving', message: '', doneAt: null } });
            const res = await createPortalItem({
                itemCode: f.itemCode,
                description: f.description.trim(),
                fullName: (f.fullName || f.description).trim(),
                upc: f.upc, upc2: f.upc2, caseUpc: f.caseUpc, um: f.um,
                weight: Number(f.weight), length: Number(f.length), height: Number(f.height), width: Number(f.width),
                preSale: f.preSale ? 1 : 0,
                companies: companyOrder(d).map((id) => ({ idCompany: id })),
                active: 0, // se activa al tener costo + precio (ver documento)
                createdBy: this.userName,
            });
            if (this._unmounted) return false;
            this.patchProduct({ portalTask: res.ok ? { status: 'done', message: '', doneAt: new Date().toISOString() } : { status: 'failed', message: res.message, doneAt: null } });
            if (!res.ok) return false;
        }
        const d2 = this.state.draft;
        if (d2.product.imageName && d2.product.imageTask.status !== 'done') {
            if (!this.state.imageFile) return false;
            this.patchProduct({ imageTask: { status: 'saving', message: '', doneAt: null } });
            const img = await uploadItemImage(f.itemCode, this.state.imageFile.name, this.state.imageFile.dataUrl);
            if (this._unmounted) return false;
            this.patchProduct({ imageTask: img.ok ? { status: 'done', message: '', doneAt: new Date().toISOString() } : { status: 'failed', message: img.message, doneAt: null } });
            return img.ok;
        }
        return true;
    }

    createItem = async () => {
        if (this.state.busy) return;
        const d = this.state.draft;
        const id = this.steps()[this.currentIndex()].company;
        const isMain = Number(id) === Number(d.mainCompany);
        const stepId = `item-${id}`;

        if (isMain && !d.companies[id].item.task.sent && d.companies[id].item.task.status !== 'done') {
            const errs = validateProductForm(d.product.form);
            if (Object.keys(errs).length) {
                this.setStepErrors(stepId, errs);
                this.setState({ dupError: 'Fix the highlighted fields.' });
                return;
            }
            if (this.state.codeCheck.status === 'taken' || this.state.upcCheck.status === 'taken') {
                this.setState({ dupError: [this.state.codeCheck, this.state.upcCheck].filter((c) => c.status === 'taken').map((c) => c.message).join(' ') });
                return;
            }
        }

        this.setBusy(true);
        this.setState({ dupError: '', dupWarning: '' });
        try {
            if (isMain && !d.companies[id].item.task.sent && d.companies[id].item.task.status !== 'done') {
                const dup = await this.checkDuplicates();
                if (this._unmounted) return;
                if (dup.error) { this.setState({ dupError: dup.error }); return; }
                if (dup.warning) this.setState({ dupWarning: dup.warning });
            }
            if (this.state.draft.companies[id].item.task.status !== 'done') {
                const t = await this.runItemTask(id);
                if (this._unmounted || t.status !== 'done') return;
            }
            if (isMain) await this.runPortalItem();
        } finally {
            this.setBusy(false);
        }
    };

    // ── Paso: precios ────────────────────────────────────────────────────────
    onPriceField(id, field, value) {
        const p = this.state.draft.companies[id].prices;
        const portalTask = p.portalTask.status === 'failed' ? emptyPortalTask() : p.portalTask;
        this.patchCompany(id, 'prices', { form: { ...p.form, [field]: value }, portalTask });
        this.clearFieldError(`prices-${id}`, field);
    }

    savePrices = async (id) => {
        if (this.state.busy) return;
        const d = this.state.draft;
        const p = d.companies[id].prices;
        const floor = priceFloor(d, id, this.state.parentOf);
        const errs = validatePricesForm(p.form, floor);
        if (Object.keys(errs).length) { this.setStepErrors(`prices-${id}`, errs); return; }
        if (p.costTask.status === 'idle') {
            const warnings = levelOrderWarnings(p.form);
            if (warnings.length) {
                const r = await Swal.fire({
                    icon: 'question', title: 'Unusual price order',
                    html: warnings.map(escapeHtml).join('<br/>') + '<br/><br/>Save anyway?',
                    showCancelButton: true, confirmButtonText: 'Save anyway', confirmButtonColor: '#2563EB', cancelButtonColor: '#64748B',
                });
                if (!r.isConfirmed) return;
            }
        }

        this.setBusy(true);
        try {
            if (p.costTask.status !== 'done') {
                const c = d.companies[id];
                const t = await runSageTask({
                    task: p.costTask,
                    entity: 'item',
                    idCompany: id,
                    key: d.product.form.itemCode,
                    match: matchCost(p.form.cost),
                    payload: { OrderType: ORDER_TYPES.cost, IdCompany: id, ItemCode: d.product.form.itemCode, VendorNo: vendorOf(c).code, UnitCost: Number(p.form.cost) },
                    onUpdate: (task) => this.patchCompany(id, 'prices', { costTask: task }),
                    wait: this.wait,
                    isCancelled: this.isCancelled,
                });
                if (this._unmounted || t.status !== 'done') return;
            }
            const f = this.state.draft.companies[id].prices.form;
            this.patchCompany(id, 'prices', { portalTask: { status: 'saving', message: '', doneAt: null } });
            const res = await savePortalPricing(d.product.form.itemCode, id, {
                level1Price: f.level1Price, level2Price: f.level2Price, level3Price: f.level3Price, level4Price: f.level4Price,
            });
            if (this._unmounted) return;
            this.patchCompany(id, 'prices', { portalTask: res.ok ? { status: 'done', message: '', doneAt: new Date().toISOString() } : { status: 'failed', message: res.message, doneAt: null } });
        } finally {
            this.setBusy(false);
        }
    };

    skipPrices = async (id) => {
        const item = this.state.draft.product.form.itemCode;
        const r = await Swal.fire({
            icon: 'warning',
            title: 'Continue without prices?',
            html: `If you continue, <b>${escapeHtml(item)}</b> will stay <b>inactive in ${escapeHtml(companyName(id))}</b> and won't show in the catalog until it has a sale price and a cost.<br/><br/>You can set them later in Price Management.`,
            showCancelButton: true, confirmButtonText: 'Continue without prices', cancelButtonText: 'Enter prices',
            confirmButtonColor: '#D97706', cancelButtonColor: '#2563EB',
        });
        if (r.isConfirmed) this.patchCompany(id, 'prices', { skipped: true });
    };

    // ── Borrador / fin ───────────────────────────────────────────────────────
    resume = () => {
        this.setState((prev) => ({ draft: prev.resumeCandidate, resumeCandidate: null }));
    };

    startOver = async (fromDraft) => {
        const d = fromDraft || this.state.draft;
        if (d && anythingSent(d)) {
            const r = await Swal.fire({
                icon: 'warning', title: 'Discard this item?',
                html: 'Some records were already sent to Sage and <b>will stay there</b>. Discarding only clears this wizard.',
                showCancelButton: true, confirmButtonText: 'Discard', confirmButtonColor: '#EF4444', cancelButtonColor: '#64748B',
            });
            if (!r.isConfirmed) return;
        }
        clearDraft(this.userName);
        this.setState({ draft: newDraft(sessionCompany()), resumeCandidate: null, finished: false, errors: {}, imageFile: null, imageError: '', dupError: '', dupWarning: '', lists: { productLine: {}, vendor: {} }, numbers: {}, codeSearch: emptySearch(), codeCheck: idleCheck(), upcCheck: idleCheck() });
    };

    finish = () => {
        clearDraft(this.userName);
        this.setState({ finished: true });
    };

    onMockMode = (e) => {
        setMockMode(e.target.value);
        this.setState({ mockMode: e.target.value });
    };

    // ── Render ───────────────────────────────────────────────────────────────
    renderStepper(steps, index, maxIndex) {
        const { draft, busy } = this.state;
        const step = steps[index];
        const pct = Math.round((index / (steps.length - 1)) * 100);
        return (
            <nav aria-label="Wizard progress" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '0.85rem', color: C.muted }}>
                        Step <b style={{ color: C.ink }}>{index + 1}</b> of {steps.length}{step.group ? ` · ${step.group}` : ''}
                    </div>
                    <button type="button" style={{ ...BTN_SECONDARY, minHeight: '36px', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }} onClick={() => this.setState((p) => ({ showStatus: !p.showStatus }))} aria-expanded={this.state.showStatus}>
                        {this.state.showStatus ? 'Hide status' : 'Status'}
                    </button>
                </div>
                <div style={{ height: '6px', background: C.line, borderRadius: '999px', marginTop: '0.5rem', overflow: 'hidden' }} aria-hidden="true">
                    <div style={{ width: `${pct}%`, height: '100%', background: C.primary, transition: 'width 0.3s' }} />
                </div>
                <ol style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', listStyle: 'none', padding: '0.6rem 0 0.25rem', margin: 0, WebkitOverflowScrolling: 'touch' }}>
                    {steps.map((s, i) => {
                        const done = isStepComplete(draft, s);
                        const active = i === index;
                        const reachable = i <= maxIndex && !busy;
                        return (
                            <li key={s.id} style={{ flexShrink: 0 }}>
                                <button
                                    type="button" disabled={!reachable || active} onClick={() => this.goTo(i)}
                                    aria-current={active ? 'step' : undefined}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.65rem', minHeight: '36px', borderRadius: '999px',
                                        fontSize: '0.75rem', fontWeight: active ? 800 : 600, whiteSpace: 'nowrap', cursor: reachable && !active ? 'pointer' : 'default',
                                        border: active ? `1.5px solid ${C.primary}` : `1px solid ${done ? C.okLine : C.line}`,
                                        background: active ? C.infoBg : done ? C.okBg : '#FFFFFF',
                                        color: active ? C.primaryDark : done ? C.ok : reachable ? C.text : '#94A3B8',
                                    }}
                                >
                                    <span aria-hidden="true">{done ? '✓' : i + 1}</span>
                                    {s.group ? `${COMPANIES[s.company].name} · ${s.label}` : s.label}
                                </button>
                            </li>
                        );
                    })}
                </ol>
            </nav>
        );
    }

    renderStep(step) {
        const { draft, busy, errors, lists, numbers, parentOf } = this.state;
        const stepErrors = errors[step.id] || {};
        const locked = isLocked(draft, step);
        if (step.type === 'companies') return <CompaniesStep draft={draft} locked={locked} onChange={this.onCompanies} />;
        if (step.type === 'productLine' || step.type === 'vendor') {
            const kind = step.type;
            const id = step.company;
            return (
                <CatalogEntityStep
                    key={step.id}
                    kind={kind} idCompany={id} role={step.role}
                    entity={draft.companies[id][kind]}
                    list={lists[kind][id] || emptyList()}
                    numberState={numbers[`${kind}-${id}`] || { loading: false, error: '' }}
                    locked={locked} busy={busy} errors={stepErrors}
                    onMode={(m) => this.onEntityMode(kind, id, m)}
                    onSelect={(row) => this.onEntitySelect(kind, id, row)}
                    onField={(f, v) => this.onEntityField(kind, id, f, v)}
                    onCreate={() => this.createEntity(kind, id)}
                    onRetry={() => this.createEntity(kind, id)}
                    onNewNumber={() => this.assignNumber(kind, id)}
                    onReloadList={() => this.ensureList(kind, id, true)}
                />
            );
        }
        if (step.type === 'item') {
            const isMain = Number(step.company) === Number(draft.mainCompany);
            return (
                <ItemStep
                    key={step.id}
                    draft={draft} idCompany={step.company} role={step.role} isMain={isMain}
                    locked={locked} busy={busy} errors={stepErrors}
                    dupError={this.state.dupError} dupWarning={this.state.dupWarning}
                    codeSearch={this.state.codeSearch} searchMinChars={SEARCH_MIN_CHARS}
                    suggestion={isMain && this.state.codeSearch.loaded ? suggestNextCode(this.state.codeSearch.rows.map((r) => r.itemCode), draft.product.form.itemCode) : null}
                    codeCheck={this.state.codeCheck} upcCheck={this.state.upcCheck}
                    onRetrySearch={() => this.scheduleSearch(draft.product.form.itemCode, 0)}
                    imageFile={this.state.imageFile} imageError={this.state.imageError}
                    onField={this.onProductField} onImage={this.onImage} onRemoveImage={this.onRemoveImage}
                    onCreate={this.createItem} onRetry={this.createItem}
                />
            );
        }
        if (step.type === 'prices') {
            const id = step.company;
            return (
                <PricesStep
                    key={step.id}
                    draft={draft} idCompany={id} role={step.role}
                    floor={priceFloor(draft, id, parentOf)}
                    isAdmin={getValueCookie('TypeUser') === '1'}
                    locked={locked} busy={busy} errors={stepErrors}
                    onField={(f, v) => this.onPriceField(id, f, v)}
                    onSave={() => this.savePrices(id)}
                    onRetry={() => this.savePrices(id)}
                    onSkip={() => this.skipPrices(id)}
                    onUndoSkip={() => this.patchCompany(id, 'prices', { skipped: false })}
                />
            );
        }
        return <SummaryView draft={draft} />;
    }

    renderActions(steps, index) {
        const { draft, busy } = this.state;
        const step = steps[index];
        const complete = isStepComplete(draft, step);
        if (step.type === 'summary') {
            return (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" style={{ ...btn(BTN_SECONDARY, busy), flex: 1 }} disabled={busy} onClick={this.back}>← Back</button>
                    <button type="button" style={{ ...BTN_PRIMARY, flex: 2 }} onClick={this.finish}>Finish</button>
                </div>
            );
        }
        const nextStep = steps[index + 1];
        const nextLabel = nextStep ? (nextStep.group ? `${nextStep.label} · ${COMPANIES[nextStep.company].name}` : nextStep.label) : '';
        return (
            <>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" style={{ ...btn(BTN_SECONDARY, busy || index === 0), flex: 1 }} disabled={busy || index === 0} onClick={this.back}>← Back</button>
                    <button type="button" style={{ ...btn(BTN_PRIMARY, busy || !complete), flex: 2 }} disabled={busy || !complete} onClick={this.next} aria-describedby={!complete ? 'next-hint' : undefined}>
                        Next{nextLabel ? `: ${nextLabel}` : ''} →
                    </button>
                </div>
                {!complete && !busy && (
                    <div id="next-hint" style={{ fontSize: '0.75rem', color: C.faint, textAlign: 'right', marginTop: '0.35rem' }}>
                        {step.type === 'prices' ? 'Save the prices or skip this step to continue.' : 'Complete and confirm this step to continue.'}
                    </div>
                )}
            </>
        );
    }

    renderResume() {
        const d = this.state.resumeCandidate;
        const updated = new Date(d.updatedAt);
        return (
            <div style={CARD}>
                <h2 style={H2}>Unfinished item</h2>
                <p style={SUB}>
                    {d.product.form.itemCode ? <><b>{d.product.form.itemCode}</b> — {d.product.form.description}</> : 'No item code yet'} · {companyOrder(d).map(companyName).join(' → ')}
                    <br />Last saved {isNaN(updated.getTime()) ? '' : updated.toLocaleString()}
                </p>
                <SummaryView draft={d} compact />
                {anythingSent(d) && <Notice kind="warn">Some records were already sent to Sage. Resume to finish them — pending checks are done against Sage before anything is resent.</Notice>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                    <button type="button" style={{ ...BTN_PRIMARY, width: '100%' }} onClick={this.resume}>Resume</button>
                    <button type="button" style={{ ...BTN_SECONDARY, width: '100%' }} onClick={() => this.startOver(d)}>Discard and start a new item</button>
                </div>
            </div>
        );
    }

    renderFinished() {
        return (
            <div style={CARD}>
                <h2 style={H2}>All done</h2>
                <p style={SUB}>The wizard was closed. Anything still pending is listed in the summary you just saw; finish it in Sage or Price Management.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                    <button type="button" style={{ ...BTN_PRIMARY, width: '100%' }} onClick={() => this.startOver(null)}>Create another item</button>
                    <a href="/inventory/products" style={{ ...BTN_SECONDARY, width: '100%', textDecoration: 'none' }}>Go to products</a>
                </div>
            </div>
        );
    }

    renderBody() {
        const { draft, resumeCandidate, finished, showStatus } = this.state;
        if (resumeCandidate) return this.renderResume();
        if (finished) return this.renderFinished();
        if (!draft) return <div style={{ ...CARD, color: C.muted }}>Loading…</div>;
        const steps = this.steps();
        const index = this.currentIndex();
        const maxIndex = maxReachableIndex(draft, steps);
        return (
            <>
                {this.renderStepper(steps, index, maxIndex)}
                {showStatus && <div style={{ ...CARD, marginBottom: '1rem', background: '#F8FAFC' }}><SummaryView draft={draft} compact /></div>}
                {this.renderStep(steps[index])}
                <div style={{ position: 'sticky', bottom: 0, background: C.bg, padding: '0.75rem 0', marginTop: '1rem', borderTop: `1px solid ${C.line}`, zIndex: 5 }}>
                    {this.renderActions(steps, index)}
                </div>
            </>
        );
    }

    render() {
        const simulated = anySimulated();
        return (
            <div className="dash-page" style={{ paddingTop: '70px', background: C.bg, minHeight: '100vh' }}>
                <Menu />
                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '1.5rem 1rem' }}>
                    <div style={{ maxWidth: '880px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <div>
                            <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.35rem', margin: 0 }}>New Item</h1>
                            <p style={{ color: 'rgba(255,255,255,0.85)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>Create a product in Sage for one or more companies, step by step.</p>
                        </div>
                        <a href="/inventory/products" style={{ color: 'white', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)', borderRadius: '8px', padding: '0.5rem 0.9rem', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', minHeight: '40px' }}>
                            <RiArrowGoBackFill aria-hidden="true" /> Products
                        </a>
                    </div>
                </div>

                <div style={{ padding: '1rem', maxWidth: '880px', margin: '0 auto' }}>
                    {simulated && (
                        <div style={{ ...CARD, background: C.warnBg, borderColor: C.warnLine, marginBottom: '1rem', padding: '0.75rem 1rem' }} role="note">
                            <div style={{ color: C.warn, fontSize: '0.85rem', fontWeight: 700 }}>Simulation mode</div>
                            <div style={{ color: C.warn, fontSize: '0.8rem', marginTop: '0.2rem' }}>Sage and the new portal endpoints are simulated — nothing is created in Sage. Switch off in newItemWizardService.js.</div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.8rem', color: C.warn, flexWrap: 'wrap' }}>
                                Simulated Sage:
                                <select value={this.state.mockMode} onChange={this.onMockMode} style={{ border: `1px solid ${C.warnLine}`, borderRadius: '6px', padding: '0.3rem 0.5rem', minHeight: '36px', background: '#FFFFFF' }}>
                                    {Object.keys(MOCK_MODES).map((k) => <option key={k} value={k}>{MOCK_MODES[k]}</option>)}
                                </select>
                            </label>
                        </div>
                    )}
                    {this.renderBody()}
                    {!this.state.resumeCandidate && !this.state.finished && this.state.draft && (
                        <p style={{ fontSize: '0.75rem', color: C.faint, marginTop: '0.5rem' }}>
                            Progress is saved on this device. If the connection drops, come back to this page to resume.{' '}
                            <button type="button" style={{ background: 'none', border: 'none', color: C.err, textDecoration: 'underline', padding: 0, fontSize: '0.75rem', cursor: 'pointer' }} disabled={this.state.busy} onClick={() => this.startOver(null)}>Discard draft</button>
                        </p>
                    )}
                </div>
            </div>
        );
    }
}
